import React, { useState, useCallback, useEffect } from "react"
import { StyleSheet, View } from "~/ui/primitives"
import { observer } from "mobx-react-lite"
import { useTheme } from '~/ui/styled'
import {
  StartFlex,
  ColumnFlex,
  CenterFlex,
  Text,
  BetweenFlex,
  IconFont,
  Button,
} from "~components/El"
import QrcodeGroup from "~components/qrcode-group"
// import useNavigation from "~hooks/useNavigation"
import { ChatPerms } from "~proto"
import { useStores } from "~models"
import { useActiveChat } from "~hooks/useActiveChat"
import { usePopup } from "~hooks/usePopup"
import { useModal } from "~hooks/useModal"
import useNavigation from "~hooks/useNavigation"
import useGroupActions from "~hooks/useGroupActions"
import { Avatar, PreviewAvatar } from "~widgets/avatar"
import { globalToast, copyText } from "~utils"
import { GroupChatInformationScreenProps} from "./types"
import ContactListView from "~pages/app-stack/home-stack/chat-list/components/ContactListView";
const MAX_SHOW = 8
// const ChatPermsLabel: Record<ChatPerms, string> = {
//   [ChatPerms.ADMIN]: "频道创建者",
//   [ChatPerms.SUB_ADMIN]: "管理员"
// }

const GroupChatInformation: React.FC<GroupChatInformationScreenProps> = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { apiStore, userStore } = useStores()
  const popup = usePopup()
  const { Dialog } = useModal()
  const { chat: current } = useActiveChat()
  const { handleAdminDissolveGroup, handleMemberLeftGroup, handleAddGroupMember } = useGroupActions()
  // const [isTop, setIsTop] = useState(false) // 置顶
  // const [mute, setMute] = useState(false) // 消息免打扰
  const [showAll, setShowAll] = useState(false)
  const [hideGroupInfo, setHideGroupInfo] = useState(false)
  // console.log('current', current)
  const destructiveButtonText = current?.isCurrentUserIsAdmin ? "解散群聊" : `删除并退出`
  const canViewMemberIdentity = current?.isCurrentUserIsAdmin || current?.isCurrentUserIsManager || !hideGroupInfo
  const members = (current?.members || []).slice().sort((a, b) => {
    // 按照 群主 → 管理员 → 普通成员 排序
    const order = {
      [ChatPerms.ADMIN]: 0,
      [ChatPerms.SUB_ADMIN]: 1,
      [ChatPerms.DEFAULT]: 2,
    }
    return order[a.perm] - order[b.perm]
  })
  const memberList = showAll ? members : members.slice(0, MAX_SHOW - 1)
  const showMore = !showAll && members.length > MAX_SHOW - 1
  const renderList = [
    { type: "invite" },
    ...memberList.map((m) => ({ type: "member", data: m })),
  ]

  if (showMore) {
    renderList.push({ type: "more" })
  }

  // 群二维码
  const openQrcodeModal = useCallback(() => {
    popup.open({
      type: "center",
      content: React.createElement(QrcodeGroup, { current ,canViewMemberIdentity:canViewMemberIdentity})
    })
  }, [canViewMemberIdentity, current, popup])

  // 退出
  const handleDestructiveButtonClick = useCallback(() => {
    Dialog.show({
      title: "提示",
      content: current?.isCurrentUserIsAdmin ? "解散群聊后，所有成员将强制离开该群聊" : "删除并退出后，将不再接收此群聊信息",
      onConfirm: () => {
        if (current?.isCurrentUserIsAdmin) {
          handleAdminDissolveGroup(current)
        } else {
          handleMemberLeftGroup(current)
        }
      },
    })
  }, [Dialog, current, handleAdminDissolveGroup, handleMemberLeftGroup])


  // 添加群成员
  const openAddMemberModal = useCallback(() => {
    const p = popup.open({
      type: "center",
      content: React.createElement(ContactListView, {
        members: members,
        onConfirm: (contacts = []) => {
          handleAddGroupMember({
            chat_id: current.id,
            chat_cid: current.chat_id,
            user_ids: contacts.map(item => item.user_id),
            sender_id: userStore.currentUser.id,
          }).then(data => {
            popup.close(p.id)
            navigation.goBack()
          }).catch(error => {
            globalToast.show(error?.message || "请求失败")
          })
        },
        onClose: () => {
          popup.close(p.id)
        }
        })
    })
  }, [current?.chat_id, current?.id, globalToast, handleAddGroupMember, members, popup])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (!current?.id) return
        const res = await apiStore.getChannelGroupSettingInfo({ chat_id: current.id, type: 1 })
        if (cancelled) return
        setHideGroupInfo(!!res?.data?.hide_group_info)
      } catch {
        // ignore
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiStore, current?.chat_id])

  if (!current) return null

  const maskName = (name?: string) => {
    if (!name) return "成员"
    if (name.length <= 1) return "*"
    return `${name.slice(0, 1)}**`
  }
  
  return (
    <View style={{ width: '100%' }}>
      <CenterFlex flexDirection="column" width="100%" padding="15px" bg={theme.colors.blockBackground}>
        <PreviewAvatar size={60} avatar={current?.image} name={current?.title}/>
        <Text color={theme.colors.text} fontSize={18} fontWeight={600} padding="15px 0">{current?.title}</Text>
       
        <Button flexDirection="row" padding="3px 0" onClick={() => canViewMemberIdentity ? copyText(current?.name) : null}>
          <Text color={theme.colors.primaryText} fontSize={12} mr="6px">
            {canViewMemberIdentity ? `${current?.name || current?.chat_id || ""}` : "***"}
          </Text>
        {  canViewMemberIdentity&& <IconFont name="copy" size={12} color={theme.colors.primaryText} />}
        </Button>
        <Button
          flexDirection="row"
          width="120px"
          height="30px"
          margin="15px 0"
          borderRadius="6px"
          bg={theme.colors.primary}
          onClick={openQrcodeModal}
        >
            <IconFont name="ewm" size={12} color={theme.colors.textLight} />
            <Text color={theme.colors.textLight} fontSize={14} ml="5px">群二维码</Text>
        </Button>
      </CenterFlex>
      <ColumnFlex width="100%" padding="15px" mt="10px" bg={theme.colors.blockBackground}>
        <Text color={theme.colors.text} fontSize={16} fontWeight={600}>群公告</Text>
        <Text
        numberOfLines={2}
        color={theme.colors.secondary}
        fontSize={12}
        mt="10px"
        onClick={() =>
          navigation.navigate("GroupAnnouncementDetail", {
            chat_id: current.chat_id,
            title: "群公告",
          })
        }
        >{current?.desc || "暂无公告"}</Text>
      </ColumnFlex>
      <ColumnFlex width="100%" padding="15px" mt="10px" bg={theme.colors.blockBackground}>
        <BetweenFlex width="100%">
            <Text color={theme.colors.text} fontSize={16} fontWeight={600}>群成员</Text>
            <Text color={theme.colors.secondary} fontSize={14}>共{members.length}人</Text>
        </BetweenFlex>
        <StartFlex width="100%" mt="10px" flexWrap="wrap">
          {renderList.map((item, index) => {
            if (item.type === "invite") {
              return (
                <CenterFlex key="invite" flexDirection="column" width="20%" mb="12px">
                  <Button
                    width="40px"
                    height="40px"
                    borderRadius="20px"
                    bg={theme.colors.background}
                    onClick={openAddMemberModal}
                  >
                    <IconFont name="tjhy" size={16} color={theme.colors.text} />
                  </Button>
                  <Text fontSize={12} color={theme.colors.secondary} mt="6px">邀请</Text>
                </CenterFlex>
              )
            }

            if (item.type === "more") {
              return (
                <CenterFlex key="more" flexDirection="column" width="20%" mb="12px">
                  <Button
                    width="40px"
                    height="40px"
                    borderRadius="20px"
                    bg={theme.colors.background}
                    onClick={() => setShowAll(true)}
                  >
                    <Text fontSize={12} color={theme.colors.text}>+{members.length - 7}</Text>
                  </Button>
                  <Text fontSize={12} color={theme.colors.secondary} mt="6px">更多</Text>
                </CenterFlex>
              )
            }

            const member = item.data

            return (
              <CenterFlex key={index} flexDirection="column" width="20%" mb="12px">
                <CenterFlex position="relative">
                  <Avatar size={40} avatar={member.user?.avatar} name={member.user?.name} />

                  {[ChatPerms.ADMIN, ChatPerms.SUB_ADMIN].includes(member.perm) && (
                    <CenterFlex
                      position="absolute"
                      right="-4px"
                      top="-4px"
                      width="16px"
                      height="16px"
                      borderRadius="8px"
                      bg={theme.colors.warning}
                    >
                      {member.perm === ChatPerms.ADMIN && (
                        <IconFont name="a-Frame1561999599" color="#fff" size={8} />
                      )}
                      {member.perm === ChatPerms.SUB_ADMIN && (
                        <IconFont name="a-Frame1561999596" color="#fff" size={8} />
                      )}
                    </CenterFlex>
                  )}
                </CenterFlex>

                <Text color={theme.colors.secondary} fontSize={12} mt="6px">
                  {member.user?.name}
                </Text>
              </CenterFlex>
            )
          })}
        </StartFlex>
      </ColumnFlex>
      <ColumnFlex width="100%" padding="0 15px" mt="10px" bg={theme.colors.blockBackground}>
        <Button
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            height="48px"
            borderBottomWidth={StyleSheet.hairlineWidth}
            borderBottomColor={theme.colors.border}
            borderBottomStyle="solid"
            onClick={() => {
              navigation.navigate("SearchChatContent", {
                chat_id: current?.chat_id ?? (current?.id != null ? String(current.id) : undefined),
              })
            }}
        >
            <StartFlex>
                <IconFont name="ss" color={theme.colors.text} size={14} />
                <Text fontSize="14px" color={theme.colors.text} ml="8px">查找聊天内容</Text>
            </StartFlex>
            <IconFont name="right" color={theme.colors.secondaryText} size={14} />
        </Button>
        {/* <Button
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            height="48px"
            borderBottomWidth={StyleSheet.hairlineWidth}
            borderBottomColor={theme.colors.border}
            borderBottomStyle="solid"
            onClick={() => {
            }}
        >
            <StartFlex>
                <IconFont name="shaixuan" color={theme.colors.text} size={14} />
                <Text fontSize="14px" color={theme.colors.text} ml="8px">置顶聊天</Text>
            </StartFlex>
            <CenterFlex pr="10px">
                <Switch value={isTop} onValueChange={setIsTop} />
            </CenterFlex>
        </Button> */}
        {/* <Button
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            height="48px"
            borderBottomWidth={StyleSheet.hairlineWidth}
            borderBottomColor={theme.colors.border}
            borderBottomStyle="solid"
            onClick={() => {
            }}
        >
            <StartFlex>
                <IconFont name="sygb" color={theme.colors.text} size={14} />
                <Text fontSize="14px" color={theme.colors.text} ml="8px">消息免打扰</Text>
            </StartFlex>
            <CenterFlex pr="10px">
                <Switch value={mute} onValueChange={setMute} />
            </CenterFlex>
        </Button> */}
      </ColumnFlex>
      <ColumnFlex width="100%" padding="0 15px" mt="10px" bg={theme.colors.blockBackground}>
        {/* <Button
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            height="48px"
            borderBottomWidth={StyleSheet.hairlineWidth}
            borderBottomColor={theme.colors.border}
            borderBottomStyle="solid"
            onClick={() => {
            }}
        >
            <StartFlex>
                <IconFont name="delete" color={theme.colors.error} size={14} />
                <Text fontSize="14px" color={theme.colors.error} ml="8px">清空聊天记录</Text>
            </StartFlex>
            <IconFont name="right" color={theme.colors.secondaryText} size={14} />
        </Button> */}
        <Button
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            height="48px"
            onClick={handleDestructiveButtonClick}
        >
            <StartFlex>
                <IconFont name="delete" color={theme.colors.error} size={14} />
                <Text fontSize="14px" color={theme.colors.error} ml="8px">{destructiveButtonText}</Text>
            </StartFlex>
            <IconFont name="right" color={theme.colors.secondaryText} size={14} />
        </Button>
      </ColumnFlex>
    </View>
  )
}

export default observer(GroupChatInformation)
